import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import clsx from 'clsx';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  toast: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((type: ToastType, message: string) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const icons = {
    success: <CheckCircle className="h-5 w-5 text-green-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <Transition
            key={t.id}
            appear
            show
            enter="transition ease-out duration-200"
            enterFrom="translate-y-2 opacity-0"
            enterTo="translate-y-0 opacity-100"
          >
            <div
              className={clsx(
                'flex items-center gap-3 rounded-lg border bg-white px-4 py-3 shadow-lg dark:bg-gray-800',
                t.type === 'error' && 'border-red-200 dark:border-red-800',
                t.type === 'success' && 'border-green-200 dark:border-green-800',
                t.type === 'info' && 'border-blue-200 dark:border-blue-800'
              )}
            >
              {icons[t.type]}
              <p className="text-sm text-gray-900 dark:text-gray-100">{t.message}</p>
              <button
                onClick={() => dismiss(t.id)}
                className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </Transition>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
